import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Menu, X, Zap, Award, Clock, Shield, CheckCircle, ArrowRight, Building, Heart, Target, Eye, Users2, Lightbulb, ChevronLeft, ChevronRight, Cpu, Home, Factory, Wrench, Brain, Settings } from 'lucide-react';


// Import all 62 electrical images
const electricalImages = [];
for (let i = 1; i <= 62; i++) {
  electricalImages.push(require(`../images/electrical (${i}).jpeg`));
}

const BentechElectricals = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState({});
  const [hoveredService, setHoveredService] = useState(null);

  // Distribute images across service categories
  const serviceImages = {
    'Residential Electrical Installation': electricalImages.slice(0, 8),
    'Commercial Electrical Systems': electricalImages.slice(8, 16),
    'Industrial Electrical Solutions': electricalImages.slice(16, 24),
    'Electrical Maintenance & Repair': electricalImages.slice(24, 32),
    'Smart Home Automation': electricalImages.slice(32, 40),
    'Security & Surveillance Systems': electricalImages.slice(40, 48),
    'Solar Power Installation': electricalImages.slice(48, 56),
    'Emergency Electrical Services': electricalImages.slice(56, 62)
  };

  const services = [
    {
      title: "Residential Electrical Installation",
      description: "Complete electrical wiring, lighting installation, and power distribution for homes. From new constructions to renovations, we ensure safety and efficiency.",
      icon: <Home className="w-8 h-8" />,
      features: ["House Wiring", "Lighting Design", "Power Outlets", "Circuit Breakers"],
      color: "from-blue-500 to-blue-700",
      hasImages: true
    },
    {
      title: "Commercial Electrical Systems",
      description: "Professional electrical solutions for offices, retail spaces, and commercial buildings. Designed for reliability and energy efficiency.",
      icon: <Building className="w-8 h-8" />,
      features: ["Office Wiring", "Emergency Lighting", "Data Cabling", "Energy Management"],
      color: "from-purple-500 to-purple-700",
      hasImages: true
    },
    {
      title: "Industrial Electrical Solutions",
      description: "Heavy-duty electrical installations for factories, warehouses, and industrial facilities. Built to handle high power demands safely.",
      icon: <Factory className="w-8 h-8" />,
      features: ["3-Phase Systems", "Motor Controls", "Heavy Machinery", "Load Centers"],
      color: "from-orange-500 to-orange-700",
      hasImages: true
    },
    {
      title: "Electrical Maintenance & Repair",
      description: "Regular maintenance and emergency repairs to keep your electrical systems running smoothly and safely. Available 24/7.",
      icon: <Wrench className="w-8 h-8" />,
      features: ["Fault Finding", "System Upgrades", "Panel Repairs", "Preventive Maintenance"],
      color: "from-emerald-500 to-emerald-700",
      hasImages: true
    },
    {
      title: "Smart Home Automation",
      description: "Modern smart home solutions including automated lighting, climate control, and integrated home management systems.",
      icon: <Brain className="w-8 h-8" />,
      features: ["Smart Lighting", "Automated Controls", "Voice Integration", "App Control"],
      color: "from-cyan-500 to-cyan-700",
      hasImages: true
    },
    {
      title: "Security & Surveillance Systems",
      description: "Advanced security installations including CCTV, access control, alarm systems, and intercom solutions for maximum protection.",
      icon: <Shield className="w-8 h-8" />,
      features: ["CCTV Installation", "Access Control", "Alarm Systems", "Intercom Setup"],
      color: "from-red-500 to-red-700",
      hasImages: true
    },
    {
      title: "Solar Power Installation",
      description: "Eco-friendly solar power systems for homes and businesses. Reduce energy costs with clean, renewable energy solutions.",
      icon: <Lightbulb className="w-8 h-8" />,
      features: ["Solar Panels", "Battery Systems", "Grid Integration", "Energy Storage"],
      color: "from-yellow-500 to-yellow-700",
      hasImages: true
    },
    {
      title: "Emergency Electrical Services",
      description: "24/7 emergency response for electrical issues. Fast, reliable service when you need it most to ensure safety and minimize downtime.",
      icon: <Zap className="w-8 h-8" />,
      features: ["24/7 Availability", "Rapid Response", "Emergency Repairs", "Safety First"],
      color: "from-pink-500 to-pink-700",
      hasImages: true
    }
  ];

  const features = [
    { 
      icon: <Award className="w-8 h-8" />, 
      title: "Certified Electricians", 
      desc: "Fully qualified and licensed electrical professionals delivering expert solutions",
      color: "text-blue-600 bg-blue-50"
    },
    { 
      icon: <Shield className="w-8 h-8" />, 
      title: "Safety Guaranteed", 
      desc: "Strict adherence to electrical safety standards and regulations for peace of mind",
      color: "text-emerald-600 bg-emerald-50"
    },
    { 
      icon: <Clock className="w-8 h-8" />, 
      title: "24/7 Emergency Service", 
      desc: "Round-the-clock availability for electrical emergencies and urgent repairs",
      color: "text-purple-600 bg-purple-50"
    },
    { 
      icon: <CheckCircle className="w-8 h-8" />, 
      title: "Quality Workmanship", 
      desc: "Superior electrical installations using premium materials and proven techniques",
      color: "text-orange-600 bg-orange-50"
    }
  ];





  const whyChooseUs = [
    {
      title: "Expert Solutions",
      description: "From simple repairs to complex installations, our certified electricians deliver expert solutions tailored to your specific needs.",
      icon: <Cpu className="w-10 h-10" />,
      iconColor: "text-blue-600"
    },
    {
      title: "Premium Quality",
      description: "We use only the highest quality electrical components and materials to ensure durability, safety, and optimal performance.",
      icon: <Shield className="w-10 h-10" />,
      iconColor: "text-emerald-600"
    },
    {
      title: "Certified Professionals",
      description: "Our team consists of fully licensed electricians with comprehensive training and certifications in all aspects of electrical work.",
      icon: <Award className="w-10 h-10" />,
      iconColor: "text-purple-600"
    },
    {
      title: "Cutting-Edge Technology",
      description: "We stay current with the latest electrical technologies and smart home innovations to provide modern, efficient solutions.",
      icon: <Settings className="w-10 h-10" />,
      iconColor: "text-orange-600"
    }
  ];

  const processes = [
    {
      step: "01",
      title: "Initial Consultation",
      description: "We discuss your electrical needs, assess the site, and provide expert recommendations for your project.",
      icon: <Phone className="w-6 h-6" />,
      bgColor: "bg-gradient-to-br from-blue-500 to-blue-700"
    },
    {
      step: "02",
      title: "Detailed Planning",
      description: "Our team creates a comprehensive electrical plan with accurate cost estimates and timelines.",
      icon: <Eye className="w-6 h-6" />,
      bgColor: "bg-gradient-to-br from-emerald-500 to-emerald-700"
    },
    {
      step: "03",
      title: "Professional Installation",
      description: "Certified electricians execute the work following strict safety standards and quality protocols.",
      icon: <Zap className="w-6 h-6" />,
      bgColor: "bg-gradient-to-br from-purple-500 to-purple-700"
    },
    {
      step: "04",
      title: "Testing & Handover",
      description: "Thorough testing ensures everything works perfectly before we hand over your completed project.",
      icon: <CheckCircle className="w-6 h-6" />,
      bgColor: "bg-gradient-to-br from-orange-500 to-orange-700"
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const nextImage = (serviceTitle) => {
    const images = serviceImages[serviceTitle];
    if (images) {
      setActiveImageIndex(prev => ({
        ...prev,
        [serviceTitle]: ((prev[serviceTitle] || 0) + 1) % images.length
      }));
    }
  };

  const prevImage = (serviceTitle) => {
    const images = serviceImages[serviceTitle];
    if (images) {
      setActiveImageIndex(prev => ({
        ...prev,
        [serviceTitle]: ((prev[serviceTitle] || 0) - 1 + images.length) % images.length
      }));
    }
  };

  const ImageCarousel = ({ serviceTitle, images }) => {
    const currentIndex = activeImageIndex[serviceTitle] || 0;
    
    return (
      <div className="relative h-64 bg-gradient-to-br from-gray-800 to-gray-900 rounded-t-xl overflow-hidden group">
        <img 
          src={images[currentIndex]} 
          alt={`${serviceTitle} ${currentIndex + 1}`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          onError={(e) => {
            e.target.src = `https://via.placeholder.com/400x300/3B82F6/FFFFFF?text=${encodeURIComponent(serviceTitle)}`;
          }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        
        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={() => prevImage(serviceTitle)}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-all duration-300 opacity-0 group-hover:opacity-100"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => nextImage(serviceTitle)}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-all duration-300 opacity-0 group-hover:opacity-100"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
        
        {/* Image indicators */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-1.5">
            {images.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentIndex ? 'bg-white w-8' : 'bg-white/50 w-1.5'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-white shadow-lg border-b border-gray-100' 
          : 'bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-500'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3 group cursor-pointer">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg transform group-hover:rotate-12 transition-transform duration-300">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h3 className={`text-xl font-bold ${scrolled ? 'text-gray-900' : 'text-white'}`}>
                  BENTECH ELECTRICALS
                </h3>
                <p className={`text-xs ${scrolled ? 'text-gray-600' : 'text-white/90'}`}>
                  Powering Your Future
                </p>
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('home')} 
                className={`font-medium transition-colors hover:text-yellow-500 ${
                  scrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Home
              </button>
              <button 
                onClick={() => scrollToSection('services')} 
                className={`font-medium transition-colors hover:text-yellow-500 ${
                  scrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('about')} 
                className={`font-medium transition-colors hover:text-yellow-500 ${
                  scrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                About
              </button>
              <button 
                onClick={() => scrollToSection('process')} 
                className={`font-medium transition-colors hover:text-yellow-500 ${
                  scrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                Process
              </button>
              <button 
                onClick={() => scrollToSection('contact')} 
                className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-2.5 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-300 font-semibold"
              >
                Contact Us
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`md:hidden p-2 transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white shadow-lg border-t border-gray-200 animate-slideDown">
            <div className="px-4 py-4 space-y-2">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left py-3 text-gray-700 hover:text-yellow-600 font-medium transition-colors">
                Home
              </button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-3 text-gray-700 hover:text-yellow-600 font-medium transition-colors">
                Services
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-3 text-gray-700 hover:text-yellow-600 font-medium transition-colors">
                About
              </button>
              <button onClick={() => scrollToSection('process')} className="block w-full text-left py-3 text-gray-700 hover:text-yellow-600 font-medium transition-colors">
                Process
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-3 text-gray-700 hover:text-yellow-600 font-medium transition-colors">
                Contact
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 pt-24 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl top-10 -left-20 animate-pulse"></div>
          <div className="absolute w-96 h-96 bg-orange-500/10 rounded-full blur-3xl bottom-10 -right-20 animate-pulse delay-1000"></div>
          <div className="absolute inset-0 opacity-10">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="electrical-grid" width="60" height="60" patternUnits="userSpaceOnUse">
                  <path d="M 60 0 L 0 0 0 60" fill="none" stroke="currentColor" strokeWidth="1" className="text-yellow-500"/>
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#electrical-grid)" />
            </svg>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-12">
          <div className="inline-block mb-6 animate-bounce">
            <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-2xl transform rotate-12">
              <Zap className="w-12 h-12 text-white -rotate-12" />
            </div>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight animate-fadeIn">
            Powering Your World,
            <br />
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
              One Connection at a Time
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Expert electrical and electronic solutions for residential, commercial, and industrial needs. 
            Available 24/7 to keep your power flowing safely and efficiently.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <button 
              onClick={() => scrollToSection('services')}
              className="group bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-2xl transition-all duration-300 flex items-center space-x-2 transform hover:scale-105"
            >
              <span>Explore Our Services</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
            </button>
            <a 
              href="tel:+254797573656"
              className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-gray-900 transition-all duration-300 transform hover:scale-105"
            >
              Call Now: +254 797 573 656
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-yellow-100 rounded-full text-yellow-700 text-sm font-semibold mb-4">
              Why Choose BENTECH
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Excellence in <span className="bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">Electrical Services</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the difference with our commitment to safety, quality, and customer satisfaction
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group text-center bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 ${feature.color} rounded-2xl mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-yellow-100 to-orange-100 rounded-full text-orange-700 text-sm font-semibold mb-4">
              Our Expertise
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Comprehensive <span className="bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">Electrical Solutions</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From residential installations to industrial systems, we provide complete electrical 
              and electronic services backed by years of expertise and certified professionals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index} 
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 transform hover:-translate-y-2"
                onMouseEnter={() => setHoveredService(index)}
                onMouseLeave={() => setHoveredService(null)}
              >
                <ImageCarousel 
                  serviceTitle={service.title} 
                  images={serviceImages[service.title]} 
                />
                
                <div className="p-8">
                  <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${service.color} rounded-xl mb-4 text-white shadow-lg transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300`}>
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-yellow-500 group-hover:to-orange-500 group-hover:bg-clip-text transition-all duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                  
                  <div className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={`h-2 bg-gradient-to-r ${service.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Sets <span className="bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">Us Apart</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Discover the BENTECH ELECTRICALS advantage for all your electrical needs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index} 
                className="flex items-start space-x-6 bg-white rounded-2xl p-8 shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl flex items-center justify-center ${item.iconColor} shadow-lg`}>
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-semibold mb-4">
              Our Process
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              How We <span className="bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">Work</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our streamlined process ensures your project is completed efficiently and safely
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {processes.map((process, index) => (
              <div key={index} className="text-center group">
                <div className="relative mb-8">
                  <div className={`w-20 h-20 ${process.bgColor} rounded-2xl flex items-center justify-center text-white mx-auto shadow-xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-300`}>
                    {process.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-br from-orange-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                    {process.step}
                  </div>
                  {index < processes.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-yellow-500 to-orange-500"></div>
                  )}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full text-emerald-700 text-sm font-semibold mb-6">
                About Us
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                Your Trusted <span className="bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">Electrical Partner</span>
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                BENTECH ELECTRICALS is dedicated to delivering safe, reliable, and innovative 
                electrical solutions across Kenya. We bring fresh energy and modern expertise to 
                every project, ensuring the highest standards of quality and safety.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Our certified electricians specialize in residential, commercial, and industrial 
                electrical systems. From basic installations to complex smart home automation and 
                industrial setups, we handle every project with precision and care.
              </p>
              <p className="text-gray-700 mb-8 leading-relaxed">
                What sets us apart is our commitment to using the latest technologies, maintaining 
                strict safety standards, and providing exceptional customer service. Every installation 
                adheres to international electrical codes and local regulations for your peace of mind.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button 
                  onClick={() => scrollToSection('services')}
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center space-x-2 transform hover:scale-105"
                >
                  <span>View Our Services</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
                <a 
                  href="tel:+254797573656"
                  className="border-2 border-gray-300 text-gray-700 px-8 py-3 rounded-xl font-semibold hover:border-yellow-500 hover:text-yellow-600 transition-all duration-300 text-center transform hover:scale-105"
                >
                  Call Us Today
                </a>
              </div>
              
              {/* Key Values */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                  <Shield className="w-8 h-8 text-blue-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-1">Safety First</h4>
                  <p className="text-gray-600 text-sm">Certified & compliant</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                  <Lightbulb className="w-8 h-8 text-yellow-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-1">Modern Solutions</h4>
                  <p className="text-gray-600 text-sm">Latest technology</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                  <CheckCircle className="w-8 h-8 text-emerald-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-1">Quality Work</h4>
                  <p className="text-gray-600 text-sm">Premium standards</p>
                </div>
                <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100">
                  <Clock className="w-8 h-8 text-purple-600 mb-2" />
                  <h4 className="font-semibold text-gray-900 mb-1">24/7 Available</h4>
                  <p className="text-gray-600 text-sm">Emergency service</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div className="aspect-square bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl p-8 text-white flex flex-col justify-center items-center text-center shadow-xl transform hover:scale-105 hover:rotate-3 transition-all duration-300">
                    <Zap className="w-16 h-16 mb-4" />
                    <h3 className="text-lg font-bold mb-2">24/7 Service</h3>
                    <p className="text-blue-100 text-sm">Always available for emergencies</p>
                  </div>
                  <div className="aspect-square bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl p-8 text-white flex flex-col justify-center items-center text-center shadow-xl transform hover:scale-105 hover:rotate-3 transition-all duration-300">
                    <Shield className="w-16 h-16 mb-4" />
                    <h3 className="text-lg font-bold mb-2">Safety First</h3>
                    <p className="text-emerald-100 text-sm">Certified & compliant</p>
                  </div>
                </div>
                <div className="space-y-6 mt-12">
                  <div className="aspect-square bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl p-8 text-white flex flex-col justify-center items-center text-center shadow-xl transform hover:scale-105 hover:rotate-3 transition-all duration-300">
                    <Award className="w-16 h-16 mb-4" />
                    <h3 className="text-lg font-bold mb-2">Certified Team</h3>
                    <p className="text-orange-100 text-sm">Fully qualified electricians</p>
                  </div>
                  <div className="aspect-square bg-gradient-to-br from-purple-500 to-purple-700 rounded-2xl p-8 text-white flex flex-col justify-center items-center text-center shadow-xl transform hover:scale-105 hover:rotate-3 transition-all duration-300">
                    <Heart className="w-16 h-16 mb-4" />
                    <h3 className="text-lg font-bold mb-2">Quality Work</h3>
                    <p className="text-purple-100 text-sm">Premium materials & standards</p>
                  </div>
                </div>
              </div>
              
              {/* Floating Achievement Badge */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white shadow-2xl animate-bounce">
                <div className="text-center">
                  <Zap className="w-10 h-10 mx-auto mb-1" />
                  <div className="text-sm font-bold">TRUSTED</div>
                  <div className="text-xs">EXPERTS</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-600 to-orange-600 animate-pulse"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Power Up Your Project?
            </h2>
            <p className="text-xl text-white/90 mb-12 leading-relaxed">
              Get in touch with BENTECH ELECTRICALS today for expert electrical solutions. 
              Our certified team is ready to handle your residential, commercial, or industrial needs 24/7.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <a 
                href="tel:+254797573656"
                className="group bg-white text-orange-600 px-8 py-4 rounded-xl font-bold hover:bg-gray-50 transition-all duration-300 flex items-center space-x-2 shadow-2xl transform hover:scale-105"
              >
                <Phone className="w-5 h-5" />
                <span>Call: +254 797 573 656</span>
              </a>
              <a 
                href="mailto:bentechelectricals254@gmail.com"
                className="group border-2 border-white text-white px-8 py-4 rounded-xl font-bold hover:bg-white hover:text-orange-600 transition-all duration-300 flex items-center space-x-2 transform hover:scale-105"
              >
                <Mail className="w-5 h-5" />
                <span>Email Us</span>
              </a>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform hover:scale-105 transition-all duration-300">
                <Phone className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Quick Response</h3>
                <p className="text-white/80 text-sm">We answer your calls 24/7</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform hover:scale-105 transition-all duration-300">
                <CheckCircle className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Free Consultation</h3>
                <p className="text-white/80 text-sm">Expert advice at no cost</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 transform hover:scale-105 transition-all duration-300">
                <Award className="w-12 h-12 text-white mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">Certified Work</h3>
                <p className="text-white/80 text-sm">Quality guaranteed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-yellow-500/20 rounded-full text-yellow-400 text-sm font-semibold mb-6">
              Contact Us
            </div>
            <h2 className="text-4xl font-bold mb-6">
              Let's Power <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Your Vision</span>
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Get in touch with our expert team for professional electrical solutions
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h3 className="text-2xl font-bold mb-8 text-white">Contact Information</h3>
              <div className="space-y-8">
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-xl">
                    <Phone className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl mb-2 text-white">Phone Number</h4>
                    <a href="tel:+254797573656" className="text-gray-300 text-lg hover:text-yellow-400 transition-colors">
                      +254 797 573 656
                    </a>
                    <p className="text-gray-400 text-sm mt-2">Available 24/7 for emergencies</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-xl">
                    <Mail className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl mb-2 text-white">Email Address</h4>
                    <a href="mailto:bentechelectricals254@gmail.com" className="text-gray-300 hover:text-yellow-400 transition-colors break-all">
                      bentechelectricals254@gmail.com
                    </a>
                    <p className="text-gray-400 text-sm mt-2">We respond within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-xl">
                    <Clock className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="font-bold text-xl mb-2 text-white">Business Hours</h4>
                    <div className="space-y-1 text-gray-300">
                      <p>Monday - Friday: 7:00 AM - 8:00 PM</p>
                      <p>Saturday: 7:00 AM - 6:00 PM</p>
                      <p>Sunday: 9:00 AM - 5:00 PM</p>
                    </div>
                    <p className="text-gray-400 text-sm mt-2">Emergency services available 24/7</p>
                  </div>
                </div>
              </div>

              {/* Service Areas */}
              <div className="mt-12 bg-gray-800/50 rounded-2xl p-8 backdrop-blur-sm border border-gray-700">
                <h4 className="text-xl font-bold mb-6 text-white">Service Coverage</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-gray-300 text-sm">Residential Homes</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-gray-300 text-sm">Commercial Spaces</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-gray-300 text-sm">Industrial Facilities</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    <span className="text-gray-300 text-sm">Emergency Services</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-12 shadow-2xl border border-gray-700">
              <h3 className="text-2xl font-bold mb-8 text-white">Quick Contact</h3>
              
              <div className="space-y-6">
                <a 
                  href="tel:+254797573656"
                  className="group block w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white py-6 px-8 rounded-xl font-bold hover:shadow-2xl transition-all duration-300 text-center transform hover:scale-105"
                >
                  <Phone className="w-8 h-8 mx-auto mb-2 group-hover:animate-bounce" />
                  <div className="text-lg mb-1">Call Us Now</div>
                  <div className="text-2xl">+254 797 573 656</div>
                </a>
                
                <a 
                  href="mailto:bentechelectricals254@gmail.com"
                  className="group block w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-6 px-8 rounded-xl font-bold hover:shadow-2xl transition-all duration-300 text-center transform hover:scale-105"
                >
                  <Mail className="w-8 h-8 mx-auto mb-2 group-hover:animate-bounce" />
                  <div className="text-lg mb-1">Email Us</div>
                  <div className="text-sm break-all">bentechelectricals254@gmail.com</div>
                </a>
                
                <div className="bg-gray-700/50 rounded-xl p-6 backdrop-blur-sm">
                  <h4 className="text-lg font-bold mb-4 text-white">What to Expect:</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2"></div>
                      <span className="text-gray-300 text-sm">Immediate response from our team</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-orange-400 rounded-full mt-2"></div>
                      <span className="text-gray-300 text-sm">Free consultation and assessment</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full mt-2"></div>
                      <span className="text-gray-300 text-sm">Detailed quote within 24 hours</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                      <span className="text-gray-300 text-sm">Professional installation with warranty</span>
                    </div>
                  </div>
                </div>

                <div className="text-center pt-6 border-t border-gray-700">
                  <p className="text-gray-300 mb-4">
                    ⚡ <strong>Contact us today</strong> for expert electrical solutions!
                  </p>
                  <div className="flex items-center justify-center space-x-2 text-sm text-gray-400">
                    <Shield className="w-4 h-4" />
                    <span>Certified • Licensed • Insured • Professional Service</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Zap className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">BENTECH ELECTRICALS</h3>
                  <p className="text-gray-400 text-sm">Powering Your Future</p>
                </div>
              </div>
              <p className="text-gray-400 mb-6 max-w-md leading-relaxed">
                Your trusted partner for all electrical and electronic solutions. From residential 
                installations to industrial systems, we deliver excellence with every project.
              </p>
              <div className="flex space-x-3">
                <a href="tel:+254797573656" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-yellow-500 hover:to-orange-500 transition-all cursor-pointer">
                  <Phone className="w-5 h-5 text-yellow-400" />
                </a>
                <a href="mailto:bentechelectricals254@gmail.com" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 transition-all cursor-pointer">
                  <Mail className="w-5 h-5 text-purple-400" />
                </a>
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-emerald-500 hover:to-emerald-700 transition-all cursor-pointer">
                  <MapPin className="w-5 h-5 text-emerald-400" />
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-6 text-white">Our Services</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li className="hover:text-yellow-400 transition-colors cursor-pointer">Residential Electrical</li>
                <li className="hover:text-yellow-400 transition-colors cursor-pointer">Commercial Systems</li>
                <li className="hover:text-yellow-400 transition-colors cursor-pointer">Industrial Solutions</li>
                <li className="hover:text-yellow-400 transition-colors cursor-pointer">Smart Home Automation</li>
                <li className="hover:text-yellow-400 transition-colors cursor-pointer">Security Systems</li>
                <li className="hover:text-yellow-400 transition-colors cursor-pointer">24/7 Emergency Service</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-bold mb-6 text-white">Quick Contact</h4>
              <div className="space-y-4 text-gray-400 text-sm">
                <div className="flex items-center space-x-3">
                  <Phone className="w-4 h-4 text-yellow-400" />
                  <a href="tel:+254797573656" className="hover:text-yellow-400 transition-colors">
                    +254 797 573 656
                  </a>
                </div>
                <div className="flex items-start space-x-3">
                  <Mail className="w-4 h-4 text-purple-400 mt-1" />
                  <a href="mailto:bentechelectricals254@gmail.com" className="hover:text-yellow-400 transition-colors break-all">
                    bentechelectricals254@gmail.com
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-4 h-4 text-orange-400" />
                  <span>Available 24/7</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center text-sm">
              <p className="text-gray-400 mb-4 md:mb-0">
                &copy; 2025 BENTECH ELECTRICALS. All rights reserved.
              </p>
              <div className="flex items-center space-x-6 text-gray-400">
                <span className="hover:text-yellow-400 transition-colors cursor-pointer">Privacy Policy</span>
                <span className="hover:text-yellow-400 transition-colors cursor-pointer">Terms of Service</span>
                <span className="hover:text-yellow-400 transition-colors cursor-pointer">Sitemap</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Add custom animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out forwards;
        }
        
        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
};

export default BentechElectricals;